import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Turnstile from "../components/Turnstile";
import Seo from "../components/Seo";

const INDUSTRIES = [
  "Personal loans",
  "Installment & short-term lending",
  "BNPL (Buy Now Pay Later)",
  "Credit builder tools",
  "Checking/savings apps",
  "Debit & Credit cards",
  "Debt Consolidation & Refinance",
  "Insurtech",
  "Compliance",
  "Anti-Fraud",
  "Data Validation",
  "Other",
];

const ATTENDEE_TYPES = ["Publisher", "Network", "Solution Provider", "Advertiser"];

const POSITIONS = [
  "Founder / CEO",
  "C-Level (CMO / CFO / COO / CRO)",
  "VP / SVP",
  "Director / Head of",
  "Senior Manager",
  "Manager",
  "Individual Contributor",
  "Other",
];

const COUNTRIES = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Ireland",
  "Germany",
  "France",
  "Spain",
  "Portugal",
  "Italy",
  "Netherlands",
  "Switzerland",
  "Brazil",
  "Argentina",
  "Colombia",
  "Australia",
  "Singapore",
  "United Arab Emirates",
  "India",
  "Other",
];

// Format US phone numbers as (XXX) XXX-XXXX as the user types
const formatUSPhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const RsvpForm = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    email: "",
    phone: "",
    country: "United States",
    industry: "",
    position: "",
    attendee_type: "",
    attended_before: false,
    looking_for: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [answers, setAnswers] = useState({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  useEffect(() => {
    api.get(`/events/${slug}`).then(({ data }) => {
      setEvent(data);
      // Pre-seed answers map with empty strings for existing custom questions
      const init = {};
      (data.questions || []).forEach((q) => {
        init[q.id] = q.type === "checkbox" ? false : "";
      });
      setAnswers(init);
    }).catch(() => setServerError("Event not found"));
  }, [slug]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length !== 10) e.phone = "10-digit US number required";
    if (!form.country) e.country = "Required";
    if (!form.industry) e.industry = "Required";
    if (!form.position) e.position = "Required";
    if (!form.attendee_type) e.attendee_type = "Required";
    if (form.attended_before === null || form.attended_before === undefined || form.attended_before === "")
      e.attended_before = "Required";
    if (!form.looking_for.trim() || form.looking_for.trim().length < 10) e.looking_for = "Min 10 characters";
    if (!form.consent) e.consent = "You must agree to continue";
    // Custom event-level questions
    (event?.questions || []).forEach((q) => {
      if (!q.required) return;
      const v = answers[q.id];
      const empty = v === undefined || v === null || v === "" || (q.type === "checkbox" && v !== true);
      if (empty) e[`q_${q.id}`] = "Required";
    });
    const requireTurnstile = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (requireTurnstile && !turnstileToken) e.turnstile = "Please complete the anti-spam check";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attended_before: form.attended_before === true || form.attended_before === "yes",
        answers,
        turnstile_token: turnstileToken || undefined,
      };
      const { data } = await api.post(`/events/${slug}/rsvp`, payload);
      navigate(`/rsvp/success`, { state: { eventTitle: event.title, email: data?.email_sent_to || form.email } });
    } catch (err) {
      setServerError(err?.response?.data?.detail || "Submission failed");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  };

  if (serverError && !event) return <div className="p-12 text-white">{serverError}</div>;
  if (!event) return <div className="p-12 text-white/40 font-mono-tech">/ loading...</div>;

  if (event.rsvp_active === false) {
    return (
      <main className="grain min-h-screen flex items-center" data-testid="rsvp-not-active">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 w-full">
          <Link to={`/events/${slug}`} className="font-mono-tech text-xs text-cyber-green tracking-[0.2em] uppercase flex items-center gap-2 mb-6"><ArrowLeft size={12} /> Back to event</Link>
          <div className="hard-border bg-cyber-surface p-8 sm:p-12">
            <div className="term-label text-cyber-green">/ rsvp not open yet</div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">{event.title}</h1>
            <p className="text-white/70 mt-5 text-lg">
              RSVPs aren't open for this event yet. Head back to the event page and tap <span className="text-white">Notify me</span> to leave your name and email — we'll let you know the moment they go live.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/events/${slug}`} className="btn-neon" data-testid="rsvp-not-active-back">Back to event</Link>
              <Link to="/events" className="btn-ghost-neon" data-testid="rsvp-not-active-events">All events</Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="grain min-h-screen" data-testid="rsvp-form-page">
      <Seo title={event ? `Request an Invitation — ${event.title}` : "Request an Invitation"} description="Request an invitation to a FintechOmni private event." noindex />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-6 sm:pt-12 pb-16 sm:pb-24">
        <Link to={`/events/${slug}`} className="font-mono-tech text-xs text-cyber-yellow tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> Back to event</Link>
        <div className="mt-6 sm:mt-8 hard-border bg-cyber-surface p-5 sm:p-10 cyber">
          <div className="term-label">/ rsvp form</div>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-5xl mt-3 leading-[1.05]">
            Request an Invitation<br />
            <span className="text-cyber-yellow">{event.title}</span>
          </h1>
          <p className="text-white/60 mt-3 text-sm sm:text-base">
            Every request is reviewed by hand. If approved, you'll receive a confirmation email and SMS with your personal QR code.
          </p>

          <form onSubmit={onSubmit} className="mt-8 sm:mt-10 space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <Field label="First name" required error={errors.first_name}>
                <input data-testid="rsvp-first-name" autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="w-full px-4 py-3" />
              </Field>
              <Field label="Last name" required error={errors.last_name}>
                <input data-testid="rsvp-last-name" autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="w-full px-4 py-3" />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <Field label="Company" required error={errors.company}>
                <input data-testid="rsvp-company" autoComplete="organization" value={form.company} onChange={(e) => set("company", e.target.value)} className="w-full px-4 py-3" />
              </Field>
              <Field label="Position / seniority" required error={errors.position}>
                <select data-testid="rsvp-position" value={form.position} onChange={(e) => set("position", e.target.value)} className="w-full px-4 py-3">
                  <option value="">Choose...</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>

            <Field
              label="Email"
              required
              error={errors.email}
              helper="Your confirmation will be delivered to your email address."
            >
              <input type="email" data-testid="rsvp-email" autoComplete="email" inputMode="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="w-full px-4 py-3" />
            </Field>

            <Field
              label="Mobile (US)"
              required
              error={errors.phone}
              helper="Your QR code will be delivered by SMS to this number once approved."
            >
              <div className="flex items-stretch">
                <span className="font-mono-tech text-sm text-white border border-cyber-line border-r-0 bg-cyber-elevated px-3 flex items-center gap-2 shrink-0">
                  <Phone size={14} weight="bold" /> +1
                </span>
                <input
                  data-testid="rsvp-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={form.phone}
                  onChange={(e) => set("phone", formatUSPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="flex-1 min-w-0 px-3 sm:px-4 py-3"
                />
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <Field label="Country" required error={errors.country}>
                <select data-testid="rsvp-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="w-full px-4 py-3">
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Industry / vertical" required error={errors.industry}>
                <select data-testid="rsvp-industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} className="w-full px-4 py-3">
                  <option value="">Choose...</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
            </div>

            <Field label="What best describes your role?" required error={errors.attendee_type}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ATTENDEE_TYPES.map((t) => {
                  const active = form.attendee_type === t;
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => set("attendee_type", t)}
                      data-testid={`rsvp-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`hard-border px-2 py-3 sm:px-3 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.15em] transition-colors duration-150 leading-tight text-center min-h-[52px] flex items-center justify-center ${
                        active ? "bg-white text-cyber-black border-white" : "bg-cyber-elevated text-white hover:border-cyber-green"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Have you attended a FintechOmni event before?" required error={errors.attended_before}>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
                {[
                  { v: true, l: "Yes" },
                  { v: false, l: "No" },
                ].map((opt) => {
                  const active = form.attended_before === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.l}
                      onClick={() => set("attended_before", opt.v)}
                      data-testid={`rsvp-attended-${opt.l.toLowerCase()}`}
                      className={`hard-border px-6 py-3 font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-150 min-h-[48px] sm:flex-none ${
                        active ? "bg-white text-cyber-black border-white" : "bg-cyber-elevated text-white hover:border-cyber-green"
                      }`}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field
              label="What are you looking for?"
              required
              error={errors.looking_for}
              helper="Be specific. Our team uses this to match you with the right operators on the floor — partners, suppliers, customers, hires, etc."
            >
              <textarea
                data-testid="rsvp-looking-for"
                rows={5}
                value={form.looking_for}
                onChange={(e) => set("looking_for", e.target.value)}
                placeholder="e.g. We're a personal-loan lender doing $4M/mo in originations. Looking for: (1) one or two new affiliate networks with clean approval-rate signal, (2) an anti-fraud / data-validation provider, (3) intros to other lenders running cross-sell partnerships."
                className="w-full px-4 py-3"
              />
            </Field>

            {/* Event-specific custom questions, configured by admins per event */}
            {(event.questions || []).length > 0 && (
              <div className="hard-border bg-cyber-elevated p-5 sm:p-6 space-y-5" data-testid="rsvp-custom-questions">
                <div className="term-label text-cyber-green">/ a few more from the organisers</div>
                {(event.questions || []).map((q) => {
                  const err = errors[`q_${q.id}`];
                  const v = answers[q.id] ?? (q.type === "checkbox" ? false : "");
                  if (q.type === "textarea") {
                    return (
                      <Field key={q.id} label={q.label} required={q.required} error={err}>
                        <textarea
                          rows={4}
                          value={v}
                          placeholder={q.placeholder || ""}
                          onChange={(e) => setAnswer(q.id, e.target.value)}
                          className="w-full px-4 py-3"
                          data-testid={`rsvp-q-${q.id}`}
                        />
                      </Field>
                    );
                  }
                  if (q.type === "select") {
                    return (
                      <Field key={q.id} label={q.label} required={q.required} error={err}>
                        <select
                          value={v}
                          onChange={(e) => setAnswer(q.id, e.target.value)}
                          className="w-full px-4 py-3"
                          data-testid={`rsvp-q-${q.id}`}
                        >
                          <option value="">{q.placeholder || "Choose..."}</option>
                          {(q.options || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </Field>
                    );
                  }
                  if (q.type === "radio") {
                    return (
                      <Field key={q.id} label={q.label} required={q.required} error={err}>
                        <div className="flex flex-wrap gap-2">
                          {(q.options || []).map((opt) => {
                            const active = v === opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setAnswer(q.id, opt)}
                                data-testid={`rsvp-q-${q.id}-${opt}`}
                                className={`px-4 py-3 hard-border font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-150 ${
                                  active
                                    ? "bg-white text-cyber-black border-white"
                                    : "bg-cyber-surface text-white hover:border-cyber-green"
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </Field>
                    );
                  }
                  if (q.type === "checkbox") {
                    return (
                      <div key={q.id}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={v === true}
                            onChange={(e) => setAnswer(q.id, e.target.checked)}
                            className="accent-cyber-green w-5 h-5 mt-1 shrink-0"
                            data-testid={`rsvp-q-${q.id}`}
                          />
                          <span className="text-white/85 text-sm leading-relaxed">
                            {q.label} {q.required && <span className="text-cyber-yellow">*</span>}
                          </span>
                        </label>
                        {err && <div className="mt-2 ml-8 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red">/ {err}</div>}
                      </div>
                    );
                  }
                  // text / email / tel default
                  return (
                    <Field key={q.id} label={q.label} required={q.required} error={err}>
                      <input
                        type={q.type === "email" ? "email" : q.type === "tel" ? "tel" : "text"}
                        value={v}
                        placeholder={q.placeholder || ""}
                        onChange={(e) => setAnswer(q.id, e.target.value)}
                        className="w-full px-4 py-3"
                        data-testid={`rsvp-q-${q.id}`}
                      />
                    </Field>
                  );
                })}
              </div>
            )}

            <div className="hard-border bg-cyber-elevated p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="rsvp-consent"
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  className="accent-cyber-green w-5 h-5 mt-1 shrink-0"
                />
                <span className="text-white/80 text-sm leading-relaxed">
                  I agree to FintechOmni's{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-white underline decoration-cyber-green underline-offset-4 hover:text-cyber-green" data-testid="rsvp-privacy-link">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-white underline decoration-cyber-green underline-offset-4 hover:text-cyber-green" data-testid="rsvp-terms-link">
                    Terms of Use
                  </Link>
                  , and consent to receive email and text messages from FintechOmni.
                </span>
              </label>
              {errors.consent && (
                <div className="mt-2 ml-8 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red" data-testid="rsvp-consent-error">
                  / {errors.consent}
                </div>
              )}
            </div>

            {serverError && <div className="hard-border border-cyber-red text-cyber-red p-4 font-mono-tech text-xs" data-testid="rsvp-server-error">/ ERROR :: {serverError}</div>}

            <div data-testid="rsvp-turnstile-wrap">
              <Turnstile ref={turnstileRef} onToken={setTurnstileToken} action="rsvp" />
              {errors.turnstile && (
                <div className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red">/ {errors.turnstile}</div>
              )}
            </div>

            <button type="submit" disabled={submitting} className="btn-neon w-full sm:w-auto justify-center" data-testid="rsvp-submit-btn">
              {submitting ? "Submitting..." : "Request an Invitation"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

const Field = ({ label, required, error, helper, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/80">
        {label} {required && <span className="text-cyber-yellow">*</span>}
      </label>
      {error && <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red">{error}</span>}
    </div>
    {children}
    {helper && <p className="mt-2 font-mono-tech text-[11px] text-white/40 leading-relaxed">{helper}</p>}
  </div>
);

export default RsvpForm;
